import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Printer, Download, X, Loader2, AlertTriangle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import VariantBadge from "@/components/admin/VariantBadge";
import "@/styles/picking-list.css";

/**
 * PickingListModal — Iter D
 * Renders the print-friendly picking list & CSV download for a single order.
 * Content policy: NEVER shows prices, internal notes, payment history, or
 * events. All data comes from the dedicated backend endpoint which enforces
 * the same content policy server-side.
 *
 * Print strategy: we toggle `body.picking-mode` before calling window.print()
 * and restore it after — the print stylesheet hides everything except the
 * `#picking-list-root` node.
 */
export default function PickingListModal({ publicId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/orders/${publicId}/picking-list`);
        setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not load picking list.");
        onClose();
      } finally { setLoading(false); }
    })();
  }, [publicId, onClose]);

  const doPrint = useCallback(() => {
    // Feb-2026 · Picking safety.
    //   • CONFLICT (persisted sources disagree) → HARD BLOCK.
    //     Production currently has 0 conflicts, so this protects us
    //     from future corrupted data without freezing any live order.
    //   • UNKNOWN (no persisted variant, e.g. legacy pre-P3.2 orders)
    //     → warning only, Print still permitted. Prod has ~31 active
    //     unknowns today; we cannot freeze fulfillment on those.
    //     A follow-up "Confirm variant" admin workflow will let admins
    //     stamp the variant explicitly; until then, verify with customer.
    if (data && data.variant_conflict) {
      toast.error(
        "Variant conflict on this order. Verify with customer before printing the picking list."
      );
      return;
    }
    if (data && !data.effective_variant) {
      toast.warning(
        "Variant UNKNOWN on this order — verify with the customer before picking/shipping. Print will still proceed."
      );
    }
    document.body.classList.add("picking-mode");
    // Give the browser a tick to apply the class before opening the dialog.
    requestAnimationFrame(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("picking-mode"), 100);
    });
  }, []);

  const doCsv = useCallback(async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/admin/orders/${publicId}/picking-list.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `picking-list-${publicId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "CSV download failed.");
    } finally { setDownloading(false); }
  }, [publicId]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" data-testid="picking-modal-loading">
        <div className="bg-white p-6 rounded-2xl border-2 border-[var(--ink)]"><Loader2 className="animate-spin" /></div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-auto no-print" role="dialog" data-testid="picking-modal">
      <div className="w-full max-w-3xl my-4">
        {/* Phase 1d — toolbar wraps on narrow viewports so CSV/Print/Close
            controls are always fully visible at 100% browser zoom. */}
        <div className="flex flex-wrap items-center justify-between gap-2 bg-white border-2 border-[var(--ink)] rounded-t-2xl px-4 py-3 sticky top-0">
          <div className="font-display text-lg min-w-0 truncate">Picking list · {data.public_id}</div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={doCsv} disabled={downloading} className="btn-ghost text-sm" data-testid="picking-csv-btn">
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} CSV
            </button>
            <button onClick={doPrint} className="btn-red text-sm" data-testid="picking-print-btn">
              <Printer size={14} /> Print
            </button>
            <button onClick={onClose} className="btn-ghost text-sm" data-testid="picking-close-btn"><X size={14} /> Close</button>
          </div>
        </div>

        {/* Printable root — the print stylesheet keeps only this visible */}
        <div id="picking-list-root">
          <PickingListPrintable data={data} />
        </div>
      </div>
    </div>
  );
}

function PickingListPrintable({ data }) {
  const workspaceUrl = `${window.location.origin}/admin/fulfillment/${data.public_id}`;
  const variantBlocked = !!data.variant_conflict || !data.effective_variant;
  return (
    <div className="picking-list-print rounded-t-none" data-testid="picking-printable">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <h1>Picking List</h1>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            Order <b>{data.public_id}</b>
          </div>
          {/* P4 · Prominent variant badge at the top of the checklist */}
          <div style={{ marginTop: 10 }} data-testid="picking-variant-header">
            <VariantBadge
              variant={data.effective_variant}
              conflict={data.variant_conflict}
              size="xl"
            />
          </div>
          {variantBlocked && (
            <div
              className={
                "mt-3 border-2 rounded-lg px-3 py-2 text-sm font-bold uppercase tracking-wider " +
                (data.variant_conflict
                  ? "border-red-800 bg-red-50 text-red-900"
                  : "border-amber-700 bg-amber-100 text-amber-950")
              }
              data-testid={data.variant_conflict ? "picking-variant-conflict" : "picking-variant-unknown"}
              style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <AlertTriangle size={16} />
              {data.variant_conflict
                ? "🚫 VARIANT CONFLICT — DO NOT FULFILL — ORDER DATA DISAGREES"
                : "⚠ VARIANT UNKNOWN — VERIFY THE VARIANT BEFORE PICKING OR SHIPPING"}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <span className="priority-badge" data-testid="picking-priority">
            {(data.priority || "normal").replaceAll("_", " ")}
          </span>
          <div style={{ textAlign: "center" }} data-testid="picking-workspace-qr">
            <QRCodeSVG value={workspaceUrl} size={72} level="M" />
            <div style={{ fontSize: 9, marginTop: 2, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
              Scan → workspace
            </div>
          </div>
        </div>
      </div>

      <h2>Order details</h2>
      <dl className="meta-grid">
        <dt>Customer</dt><dd data-testid="picking-customer">{data.customer_name || "—"}</dd>
        <dt>Variant</dt><dd data-testid="picking-variant-summary">
          <VariantBadge variant={data.effective_variant} conflict={data.variant_conflict} size="md" />
        </dd>
        <dt>Album</dt><dd>{data.album_slug || "—"}</dd>
        <dt>Delivery</dt><dd>{(data.delivery_code || "—").replaceAll("_", " ")}</dd>
        <dt>Total stickers</dt><dd data-testid="picking-total">{data.sticker_count} across {data.unique_codes} unique code{data.unique_codes === 1 ? "" : "s"}</dd>
        <dt>Generated</dt><dd>{new Date(data.generated_at).toLocaleString()}</dd>
      </dl>

      {data.pinned_instructions && (
        <>
          <h2>Customer instructions</h2>
          <div className="instructions" data-testid="picking-instructions">{data.pinned_instructions}</div>
        </>
      )}

      <h2>Stickers to pick</h2>
      <table>
        <thead>
          <tr>
            <th className="checkbox-cell">Picked</th>
            <th>Code</th>
            <th>Variant</th>
            <th className="qty-cell">Qty</th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((l, i) => (
            <tr key={l.code + i} data-testid={`picking-row-${l.code}`}>
              <td className="checkbox-cell"><span className="picking-checkbox" aria-hidden="true"></span></td>
              <td className="code-cell">
                <div>{l.code}</div>
                {(l.display_name || l.player_name || l.subject) && (
                  <div className="picking-subject" data-testid={`picking-name-${l.code}`}>
                    {l.display_name || l.player_name || l.subject}
                  </div>
                )}
              </td>
              <td data-testid={`picking-variant-${l.code}`}>
                {/* Per-row variant. Reads ONLY the persisted line variant,
                    falls back to the resolved order-level effective
                    variant when the persisted line variant is missing.
                    Never falls back to Master Sticker DB. */}
                <VariantBadge
                  variant={l.variant || data.effective_variant}
                  conflict={!l.variant && data.variant_conflict}
                  size="sm"
                />
              </td>
              <td className="qty-cell">{l.qty}</td>
            </tr>
          ))}
          <tr className="totals-row">
            <td></td>
            <td>Total ({data.unique_codes} unique)</td>
            <td></td>
            <td className="qty-cell">{data.sticker_count}</td>
          </tr>
        </tbody>
      </table>

      <div className="signature-block" data-testid="picking-signatures">
        <div>Picker</div>
        <div>Checked By</div>
        <div>Completed At</div>
      </div>
    </div>
  );
}
